import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";
import { resetAll } from "./baseSlice";
import * as AuditLogConstants from "../../constants/AuditLogConstants";

const customIntl = intl();

const messageOf = (error) =>
  customIntl.formatMessage(
    { id: AuditLogConstants.AUDIT_ERROR_MESSAGES[error?.response] || "something_else_wrong" },
    { limit: customIntl.formatNumber(AuditLogConstants.AUDIT_EXPORT_LIMIT) }
  );

// The list's filters. The date range is kept as its choice, not as dates, so
// a list opened again tomorrow shows tomorrow's "Last 30 days".
export const initialQuery = {
  range: AuditLogConstants.AUDIT_RANGE_LAST_30_DAYS,
  customRange: null,
  kind: null,
  area: null,
  action: null,
  userId: null,
  userName: null,
  search: "",
  pageNo: 1,
  pageSize: AuditLogConstants.AUDIT_LOG_PAGE_SIZE,
  sortBy: "desc",
};

const initialState = {
  list: [],
  totalCount: 0,
  query: initialQuery,
  requesting: false,
  // A load has answered with the rows now shown (a failed load leaves it false).
  loaded: false,
  error: null,
  options: { areas: [], actions: [] },
  details: null,
  detailsRequesting: false,
  exporting: false,
};

const auditLogSlice = createSlice({
  name: "auditLogs",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    auditLogListRequest: (state) => {
      state.requesting = true;
      state.loaded = false;
      state.error = null;
    },
    auditLogListSuccess: (state, action) => {
      state.requesting = false;
      state.loaded = true;
      state.list = action.payload?.response?.list || [];
      state.totalCount = action.payload?.response?.totalCount || 0;
    },
    auditLogListFail: (state, action) => {
      state.requesting = false;
      state.loaded = false;
      state.error = action.payload;
      state.list = [];
      state.totalCount = 0;
      toast.error(messageOf(action.payload));
    },
    auditLogSetQuery: (state, action) => {
      state.query = action.payload;
    },
    auditLogClearFilters: (state) => {
      state.query = { ...initialQuery, pageSize: state.query.pageSize };
    },

    auditLogOptionsRequest: (state) => state,
    auditLogOptionsSuccess: (state, action) => {
      state.options = {
        areas: action.payload?.response?.areas || [],
        actions: action.payload?.response?.actions || [],
      };
    },
    auditLogOptionsFail: (state) => {
      state.options = { areas: [], actions: [] };
    },

    auditLogDetailsRequest: (state) => {
      state.detailsRequesting = true;
      state.details = null;
    },
    auditLogDetailsSuccess: (state, action) => {
      state.detailsRequesting = false;
      state.details = action.payload?.response || null;
    },
    auditLogDetailsFail: (state, action) => {
      state.detailsRequesting = false;
      state.details = null;
      toast.error(messageOf(action.payload));
    },
    auditLogDetailsClose: (state) => {
      state.details = null;
      state.detailsRequesting = false;
    },

    auditLogExportRequest: (state) => {
      state.exporting = true;
    },
    auditLogExportSuccess: (state) => {
      state.exporting = false;
    },
    auditLogExportFail: (state, action) => {
      state.exporting = false;
      toast.error(messageOf(action.payload));
    },
  },
});

export const {
  auditLogListRequest,
  auditLogListSuccess,
  auditLogListFail,
  auditLogSetQuery,
  auditLogClearFilters,
  auditLogOptionsRequest,
  auditLogOptionsSuccess,
  auditLogOptionsFail,
  auditLogDetailsRequest,
  auditLogDetailsSuccess,
  auditLogDetailsFail,
  auditLogDetailsClose,
  auditLogExportRequest,
  auditLogExportSuccess,
  auditLogExportFail,
} = auditLogSlice.actions;
export default auditLogSlice.reducer;
